import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import {
  PortableText,
  PortableTextBlock,
  PortableTextBlockComponent,
} from '@portabletext/react'
import Link from 'next/link'

import { clickRatesAndFees } from 'clients/segment'
import {
  AddToCompareAwards,
  CardArt,
  LinkToRedirect,
  SecureSite,
} from 'components/'
import { useComparisonCartCards } from 'components/app/comparisonCartCardsContext'
import { Stats } from 'components/entity-panel/sections/components/stats'
import { nextImageHelper, supify, getAdvertorialAttributeByCard } from 'utils/'
import type { CompareCredit } from '../../../../types'
import CardDetails from '../../awards/CardDetails'
import { ApplyNowIcon } from '../../link-to-redirect/ApplyNowIcon'

const Normal: PortableTextBlockComponent = ({
  children,
}: React.PropsWithChildren) => <p>{children}</p>

const whyWeLikeAttributeRenderer = (card: CompareCredit.FormattedCard) => {
  const renderComponent: React.FC<{
    value: {
      attribute: string
      bonusRewards: string
    }
  }> = ({ value }) => {
    return card ? (
      getAdvertorialAttributeByCard({
        card,
        ...value,
      })
    ) : (
      <></>
    )
  }

  return renderComponent
}

export const CardTile: React.FC<{
  card: CompareCredit.FormattedCard
  title?: string
  whyWeLike: PortableTextBlock[]
  orderId: string
  categoryId: string
  externalId: string
  position?: number
}> = ({
  card,
  title,
  whyWeLike,
  orderId,
  categoryId,
  externalId,
  position,
}) => {
  const [cardPanel, cardPanelOpen] = useState(false)
  const handleCardPanel = () => cardPanelOpen(!cardPanel)

  const {
    comparisonCartCards: comparisonCart,
    updateComparisonCartCards: updateComparisonCart,
  } = useComparisonCartCards()

  const [cardIsInCart, setCardIsInCart] = useState(false)

  const cardIsInCartMemo = useMemo(
    () =>
      comparisonCart.some(
        (comparisonCartCard) => comparisonCartCard.slug === card.slug,
      ),
    [comparisonCart, card],
  )

  useEffect(() => {
    setCardIsInCart(cardIsInCartMemo)
  }, [cardIsInCartMemo])

  const handleUpdateCart = (action: 'add' | 'remove') => {
    updateComparisonCart(action, [card])
  }

  return (
    <div
      className={`c-card-tile / c-product-panel / pt-3 md:pt-0 mb-6 / border border-slate-300 ${
        cardPanel ? '' : 'is-closed'
      }`}
    >
      {title && (
        <div className="c-card-tile__badge / relative / flex items-center justify-center / mb-3 / px-14 / uppercase text-primary font-bold text-center tracking-wide leading-snug text-sm / md:hidden">
          <span className="absolute top-[-4px] left-3 xs:left-5 / shrink-0 / w-6 / mr-2">
            <Image
              width={16}
              height={16}
              alt="CompareCredit Awards Ribbon"
              src="/static/awards/cc-awards-ribbon-small-clr.svg"
              aria-hidden={true}
              sizes="100vw"
              style={nextImageHelper.responsive}
            />
          </span>{' '}
          {title}
        </div>
      )}
      <div className="md:flex md:flex-row md:items-stretch / px-4 xs:px-6 md:px-4 lg:px-0">
        <div
          className="c-card-tile__col-1 / md:shrink-0 md:w-[30%] lg:w-[25%] xl:w-1/3 / md:pr-6 md:pt-6 /
        lg:px-6 xl:px-18 / xl:pt-6 / lg:border-r lg:border-slate-200 pb-4"
        >
          <div className="c-card-art / w-full max-w-[16rem] lg:max-w-none / mb-4 lg:mb-3 / text-center / flex items-center justify-center / mx-auto">
            <div className="relative / w-full / rounded-sm shadow-lg">
              <LinkToRedirect
                externalId={externalId}
                linkParams={card.applyNowLinkParameters}
                orderId={orderId}
                query={undefined}
                slug={card.slug}
              >
                <div className="relative flex rounded overflow-hidden leading-tight">
                  <span className="c-card-art__content / absolute / flex flex-wrap content-center items-center / z-10 left-0 / w-full h-full text-white lg:text-lg">
                    <span className="w-full">
                      <div className="inline-block / w-8 h-8 md:w-6 md:h-6 lg:w-8 lg:h-8">
                        <ApplyNowIcon
                          height={32}
                          width={32}
                          style={nextImageHelper.responsive}
                          sizes="100vw"
                        />
                      </div>
                    </span>
                    <span className="w-full font-semibold">Apply Now</span>
                  </span>

                  <CardArt
                    cardArt={card.cardArt}
                    imgClasses="relative / z-0 / w-full h-full object-cover top-0"
                    customCodeSnippets={card.customCodeSnippets}
                    issuer={card.issuer.slug.current}
                    name={card.name}
                    orderId={orderId}
                    slug={card.slug}
                    type={card._type}
                    position={position}
                    categoryId={categoryId}
                    externalId={externalId}
                    _rev={null}
                  />
                </div>
              </LinkToRedirect>
            </div>
          </div>
          <div className="c-card-tile__name / mx-auto mb-3 leading-tight / md:hidden">
            <LinkToRedirect
              externalId={externalId}
              linkParams={card.applyNowLinkParameters}
              orderId={orderId}
              query={undefined}
              slug={card.slug}
            >
              <div
                className="inline-block / w-full / text-lg text-center text-primary-mid font-bold /
                          hover:text-primary-bright cursor-pointer / transition-all"
                dangerouslySetInnerHTML={{
                  __html: supify(card.name),
                }}
              />
            </LinkToRedirect>
          </div>
          <div className="c-card-tile__action">
            <div className="mb-2.5">
              <LinkToRedirect
                externalId={externalId}
                linkParams={card.applyNowLinkParameters}
                orderId={orderId}
                query={undefined}
                slug={card.slug}
              >
                <span className="c-btn c-btn--primary max-w-none / px-0 py-2.5 md:py-1.5 xl:py-1 / w-full / text-lg xl:text-xl / shadow hover:shadow-lg">
                  Apply Now
                  <span className="w-5 h-5 ml-3 / inline-block">
                    <ApplyNowIcon
                      height={32}
                      width={32}
                      aria-hidden={true}
                      style={nextImageHelper.responsive}
                      sizes="100vw"
                    />
                  </span>
                </span>
              </LinkToRedirect>
            </div>
            <p className="text-fs13 text-gray-600 text-center">
              <SecureSite
                issuerName={card.issuer.name}
                issuerSecureSite={card.issuerSecureSite}
              />
            </p>
            <div className="mb-2">
              {card.ratesAndFees && card.ratesAndFees && (
                <div className="w-full / text-center">
                  {card.ratesAndFees && card.ratesAndFees !== 'N/A' && (
                    <Link
                      className="inline-block text-primary-bright text-fs12 leading-snug"
                      href={card.ratesAndFees}
                      onClick={() => {
                        clickRatesAndFees({
                          applyNowLink: card.applyNowLink,
                          component: 'Card Comparison Page Navigation Tile',
                          name: card.name,
                          url: window.location.href,
                        })
                      }}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Rates &amp; Fees
                    </Link>
                  )}
                  {card.issuer.slug.current === 'american-express' && (
                    <p className="text-fs10 text-gray-600 / text-center / w-full leading-snug">
                      Terms Apply
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <AddToCompareAwards
            card={card}
            inCart={cardIsInCart}
            compact
            updateCart={handleUpdateCart}
          />
        </div>
        <div className="c-card-tile__col-2 / lg:flex lg:flex-col lg:items-start lg:justify-between / lg:w-[75%] / md:pl-2 md:pt-6 lg:pb-4 lg:px-6 xl:px-12 xl:pb-6">
          <div>
            {title && (
              <div
                className="c-card-tile__badge / flex-row items-center justify-start / mb-3 /
              text-sm xl:text-base uppercase text-primary font-bold text-left tracking-wider leading-tight / hidden md:flex"
              >
                <span className="inline-block / grow-0 shrink-0 / w-6 md:w-[1.25rem] xl:w-[1.5rem] / mr-3">
                  <Image
                    width={16}
                    height={16}
                    alt="CompareCredit Awards Ribbon"
                    src="/static/awards/cc-awards-ribbon-small-clr.svg"
                    aria-hidden={true}
                    sizes="100vw"
                    style={nextImageHelper.responsive}
                  />
                </span>{' '}
                <span>{title}</span>
              </div>
            )}
            <p className="c-card-tile__name / w-full / mx-auto mb-3 md:mb-4 leading-tight / hidden md:block">
              <LinkToRedirect
                externalId={externalId}
                linkParams={card.applyNowLinkParameters}
                orderId={orderId}
                query={undefined}
                slug={card.slug}
              >
                <span
                  className="inline-block / w-full / text-[22px] xl:text-[24px] text-primary-mid font-bold /
                          hover:text-primary-bright cursor-pointer / transition-all"
                  dangerouslySetInnerHTML={{
                    __html: supify(card.name),
                  }}
                />
              </LinkToRedirect>
            </p>
            <ul className="md:flex md:flex-row md:flex-wrap / md:ml-[-.5rem] / border-t-3 border-slate-200 md:border-none / text-sm leading-[1.5] text-slate-600">
              <Stats entity={card} view="details" category={card.category} />
            </ul>
          </div>
          <div
            className={`c-card-tile__details c-card-tile__details--secondary c-highlight__item--hidden / transition-all`}
          >
            <CardDetails
              card={card}
              orderId={orderId}
              view={'list'}
              isCollapsed={false}
            />
          </div>

          <div className="c-card-tile__expand-wrap / w-full / pt-2 / hidden lg:block">
            <button
              className={`c-card-tile__expand-toggle / flex items-center justify-center / w-full / px-4 py-2 lg:py-1.5 xl:py-2.5 /
                  text-primary-bright font-semibold / border border-primary-bright /
                  hover:bg-primary-light-bg hover:text-primary-mid transition-all`}
              onClick={handleCardPanel}
            >
              Show{` `}
              {cardPanel ? 'less' : 'more'}
              {` `}
              details
              <span
                className={`w-4 ml-2 / inline-block ${
                  cardPanel ? 'rotate-180' : ''
                }`}
              >
                <Image
                  height={32}
                  width={32}
                  alt="chevron"
                  src="/static/icons/icon-chevron-d-primary-bright.svg"
                  aria-hidden={true}
                  style={nextImageHelper.responsive}
                  sizes="100vw"
                />
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        className={`c-card-tile__details c-card-tile__details--base c-highlight__item--hidden / transition-all`}
      >
        <CardDetails
          card={card}
          orderId={orderId}
          view={'list'}
          isCollapsed={false}
        />
      </div>

      <div className="c-card-tile__expand-wrap / px-4 xs:px-6 pt-4 pb-6 md:pt-4 / lg:hidden">
        <button
          className={`c-card-tile__expand-toggle / flex items-center justify-center / w-full / px-4 py-2 /
                  text-primary-bright font-semibold / border border-primary-bright /
                  hover:bg-primary-light-bg hover:text-primary-mid transition-all`}
          onClick={handleCardPanel}
        >
          Show{` `}
          {cardPanel ? 'less' : 'more'}
          {` `}
          details
          <span
            className={`w-4 ml-2 / inline-block ${
              cardPanel ? 'rotate-180' : ''
            }`}
          >
            <Image
              height={32}
              width={32}
              alt="chevron"
              src="/static/icons/icon-chevron-d-primary-bright.svg"
              aria-hidden={true}
              style={nextImageHelper.responsive}
              sizes="100vw"
            />
          </span>
        </button>
      </div>
      <div className="c-card-tile__summary / flex flex-row / px-4 xs:px-6 py-3.5 lg:pb-3 xl:px-10 / bg-primary-light-bg">
        <span className="shrink-0 w-5 mr-1 / inline-block">
          <Image
            height={32}
            width={32}
            alt="thumbs up"
            src="/static/icons/icon-thumbs-up-primary-bright.svg"
            aria-hidden={true}
            style={nextImageHelper.responsive}
            sizes="100vw"
          />
        </span>
        <div className="pl-2">
          <p className="mb-1 text-primary uppercase text-sm xl:text-base font-bold tracking-wide">
            Why We Like It
          </p>
          <div className="text-sm xl:text-base text-slate-700">
            <PortableText
              value={whyWeLike}
              components={{
                block: {
                  normal: Normal,
                },
                types: {
                  advertorialAttribute: whyWeLikeAttributeRenderer(card),
                },
              }}
            />
          </div>
        </div>
      </div>
      <style jsx>{`
        @media (max-width: 1023px) {
          .c-card-tile__details--secondary {
            display: none !important;
          }
        }
        @media (min-width: 1024px) {
          .c-card-tile__details--base {
            display: none !important;
          }
        }
      `}</style>
    </div>
  )
}
